import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { TextFormat, Translate, getSortState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC } from 'app/shared/util/pagination.constants';
import { overrideSortStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './customer.reducer';

export const Customer = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [sortState, setSortState] = useState(
    overrideSortStateWithQueryParams(getSortState(pageLocation, 'id'), pageLocation.search),
  );

  const customerList = useAppSelector(state => state.customer.entities);
  const loading = useAppSelector(state => state.customer.loading);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        sort: `${sortState.sort},${sortState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?sort=${sortState.sort},${sortState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [sortState.order, sortState.sort]);

  const sort = p => () => {
    setSortState({
      ...sortState,
      order: sortState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = sortState.sort;
    const order = sortState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="customer-heading" data-cy="CustomerHeading">
        <Translate contentKey="crawlerBackendApp.customer.home.title">Customers</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="crawlerBackendApp.customer.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/customer/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="crawlerBackendApp.customer.home.createLabel">Create new Customer</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {customerList && customerList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="crawlerBackendApp.customer.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('customerType')}>
                  <Translate contentKey="crawlerBackendApp.customer.customerType">Customer Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('customerType')} />
                </th>
                <th className="hand" onClick={sort('customerStatus')}>
                  <Translate contentKey="crawlerBackendApp.customer.customerStatus">Customer Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('customerStatus')} />
                </th>
                <th className="hand" onClick={sort('sendToMandate')}>
                  <Translate contentKey="crawlerBackendApp.customer.sendToMandate">Send To Mandate</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('sendToMandate')} />
                </th>
                <th className="hand" onClick={sort('createdTimestamp')}>
                  <Translate contentKey="crawlerBackendApp.customer.createdTimestamp">Created Timestamp</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createdTimestamp')} />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.customer.customerAccount">Customer Account</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.customer.organisation">Organisation</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.customer.suppliers">Suppliers</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {customerList.map((customer, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/customer/${customer.id}`} color="link" size="sm">
                      {customer.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.CustomerType.${customer.customerType}`} />
                  </td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.CustomerStatus.${customer.customerStatus}`} />
                  </td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.SendToMandateStatus.${customer.sendToMandate}`} />
                  </td>
                  <td>
                    {customer.createdTimestamp ? (
                      <TextFormat type="date" value={customer.createdTimestamp} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {customer.customerAccount ? (
                      <Link to={`/customer-account/${customer.customerAccount.id}`}>{customer.customerAccount.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {customer.organisation ? (
                      <Link to={`/organisation/${customer.organisation.id}`}>{customer.organisation.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {customer.suppliers
                      ? customer.suppliers.map((val, j) => (
                          <span key={j}>
                            <Link to={`/supplier/${val.id}`}>{val.id}</Link>
                            {j === customer.suppliers.length - 1 ? '' : ', '}
                          </span>
                        ))
                      : null}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/customer/${customer.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/customer/${customer.id}/edit`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() => (window.location.href = `/customer/${customer.id}/delete`)}
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="crawlerBackendApp.customer.home.notFound">No Customers found</Translate>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Customer;
